// A quién graba Clarity dentro de la app (UD-7 de /ux, 18/10/2026): el alta y las agencias en prueba
// o con la prueba vencida sin pagar. Nunca el portal (usuario de cliente), un superadmin ni una
// agencia paga. La política de privacidad del sitio (`dashbo-sitio/content/terminos-y-privacidad.md`,
// «Analítica de comportamiento del usuario») dice exactamente esto: si cambia acá, cambia allá.

export const RECORDED_STATUSES = {
    'Free Trial': 'prueba',
    'Never Active': 'vencida',
};

// El alta en sí: antes de tener cuenta (`/signup`), «Crea tu agencia» (`/agency-register`, con sesión
// pero sin agencia) y el túnel. Mientras no hay respuesta del backend para una sesión recién abierta,
// la grabación que venía del `/signup` sólo sigue acá (`ClarityTracker`). Con suscripción manda el
// estado de la agencia, también en el túnel (una agencia paga que vuelve a `/onboarding/ia` no se graba).
const SIGNUP_PATH = '/signup';
const AGENCY_REGISTER_PATH = '/agency-register';
const TUNNEL_PATH = '/onboarding';

export function isSignupPath(pathname) {
    return pathname === SIGNUP_PATH || pathname === AGENCY_REGISTER_PATH
        || pathname === TUNNEL_PATH || pathname.startsWith(`${TUNNEL_PATH}/`);
}

// ⚠️ Clarity guarda la URL ENTERA de cada página (`location.href`, con la query), y el modo estricto
// no la tapa. Una URL que lleva un secreto o un dato no se graba:
// - por el path: el link público de un informe (`/r/<uuid>`), la invitación (`/accept-invitation/<hash>`),
//   el canje del MCP (`/auth/…`), las vueltas de OAuth (`/connect-*`, `/escalafy/…`, con `code`,
//   `state` o el dominio de la tienda) y el link de Mercado Ads para el vendedor (`/conectar/<token>`:
//   con el token, cualquiera conecta una cuenta a ese cliente durante 7 días);
// - por la query: sólo pasan las claves de esta lista, que llevan ids o valores fijos. Una clave
//   nueva queda afuera hasta que alguien la sume (falla del lado seguro): `q` es lo que se tipea en
//   una búsqueda, y `token`, `code`, `shop`, `agency_name` o `message` llevan datos.
const PRIVATE_PATH_PREFIXES = ['/r/', '/accept-invitation/', '/auth/', '/connect-', '/escalafy/', '/conectar/'];

const ALLOWED_QUERY_KEYS = new Set([
    'just_connected', 'oauth_error', 'oauth_reason', 'connect', 'assistant', 'embed',
    'client', 'client_id', 'view_id', 'replicated_from',
    'reconnect_pending', 'reconnect_ad_account_id', 'reconnect_acc_id',
    'vista', 'plataforma', 'filtro', 'ignore', 'tarea', 'mode', 'kind', 'layers', 'columns', 'channel',
    'v', 'date_from', 'date_to', 'guide', 'guide_preview', 'showAddAccountModal', 'urls_modal',
]);

export function isPrivateUrl({pathname = '', search = '', hash = ''}) {
    if (PRIVATE_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return true;
    for (const key of new URLSearchParams(search).keys()) {
        if (!ALLOWED_QUERY_KEYS.has(key)) return true;
    }
    // Un ancla (`#memoria`) pasa; un fragmento con datos (`#access_token=…`) no.
    return /[=&]/.test(hash);
}

/**
 * El `estado` con el que se graba esta página (`alta`, `prueba` o `vencida`), o null si no se graba.
 *
 * `session` es null sin sesión. Con sesión, sale de `get-logged-user-data/` y NO de LocalStorage:
 * el flag de superadmin se borra en cada login y se rellena recién con esa respuesta, así que leerlo
 * de LocalStorage dejaría grabar a un superadmin parado en una agencia en prueba.
 */
export function clarityAudience({pathname, search = '', hash = '', session}) {
    if (isPrivateUrl({pathname, search, hash})) return null;
    if (!session) return pathname === SIGNUP_PATH ? 'alta' : null;
    if (session.userType !== 'AGENCY_USER') return null;
    // Sólo un `false` explícito: un backend que no manda el dato no abre la puerta.
    if (session.isSuperAdmin !== false) return null;

    const estado = RECORDED_STATUSES[session.agencyStatus];
    if (estado) return estado;

    // Sin suscripción todavía el backend manda el estado en null (`_get_agency_status`): una cuenta
    // recién creada, en cualquier pantalla. No alcanza con el túnel: la prueba nace al crear la
    // agencia, la respuesta queda 30 s en la caché del `APIClient`, y quien sale rápido del túnel a
    // `/agency/clients` se dejaba de grabar justo en su primera vista.
    if (session.agencyStatus === null || session.agencyStatus === undefined) return 'alta';
    return null;
}
